/**
 * @file Foot Facts report — modal mount + triggers (decision D8, entry point a).
 *
 * Presents the Foot Facts report (Variant A renderer) in an accessible native
 * <dialog> modal, fed by either data path:
 *
 *   1. Post-scan (in-memory): volumental-widget.liquid dispatches `footfacts:ready`
 *      after a scan completes (same coupling pattern as `size:recommended`); this
 *      component then reveals the trigger button that the [Custom] Foot Facts block
 *      renders in its own markup. No DOM is injected into other components.
 *   2. Deep link (Live re-fetch, decision D9): a `?scan=<publicID>` URL param makes
 *      the page load the scan via FootFactsLoader and auto-open the report.
 *
 * Initialized by blocks/foot-facts-report.liquid:
 *   window.FootFactsReport.init({ gender, visaToken })
 * gender comes from the product's tags (male-product / female-product → 'male' /
 * 'female', else null → the report's gender-neutral copy).
 *
 * Requires foot-facts-calculator.js, arch-height-calculator.js, foot-facts-loader.js
 * and foot-facts-render-email.js to be loaded first (the block loads all of them).
 */
(function () {
  'use strict';

  // Guard against multiple loads — a duplicate include must not reset report state
  // or re-register listeners.
  if (window.FootFactsReport) return;

  const config = { gender: null, visaToken: null, supportEmail: 'care@fitmyfoot.com', paramName: 'scan', gateParam: 'ff', storageKey: 'footFactsCohort', sharePath: '/pages/foot-facts-report' };
  let lastFootFacts = null;
  let lastScanId = null;

  // Selective-enablement gate (D14/D16/D18) — ALLOWLIST semantics: the feature is
  // enabled only for customers who arrive via a communicated link carrying
  // ?ff=<cohort>, where the cohort value must be REGISTERED in COHORT_CONFIG below.
  // Unregistered values are inert (nothing enabled, nothing persisted). A registered
  // cohort is persisted (localStorage) so the feature stays enabled for that browser
  // param-free thereafter; the stored value is re-validated against the registry on
  // every read, so REMOVING a cohort from COHORT_CONFIG revokes it everywhere on next
  // page load. ?ff=off (or ?ff=0) clears the browser explicitly. ?scan= deep links do
  // NOT enable the feature (a scan link is not an eligibility grant). Test tools set
  // window.FOOTFACTS_FORCE (session-only, never persisted). localStorage access is
  // guarded (Safari private mode can throw).
  const GATE_CLEAR_VALUES = ['0', 'off'];

  // Cohort registry + allowlist (D17/D18): keys are the values carried by ?ff=<cohort>;
  // flags define per-cohort behavior (absent flags = standard behavior). Never register
  // 'test' (reserved: the simulator's force flag reports it as a pseudo-cohort) or the
  // clear values. Registry doc: docs/projects/foot-facts-web-port/COHORTS.md.
  const COHORT_CONFIG = {
    s: { trimRescanCopy: true },
  };

  const isRegistered = (value) =>
    value !== null && Object.prototype.hasOwnProperty.call(COHORT_CONFIG, value);

  function storageGet() {
    try { return window.localStorage.getItem(config.storageKey); } catch (e) { return null; }
  }

  function paramValue() {
    return new URLSearchParams(window.location.search).get(config.gateParam);
  }

  function syncGateFromUrl() {
    const value = paramValue();
    if (value === null) return;
    try {
      if (GATE_CLEAR_VALUES.indexOf(value) !== -1) {
        window.localStorage.removeItem(config.storageKey);
      } else if (isRegistered(value)) {
        window.localStorage.setItem(config.storageKey, value);
      }
      // Unregistered values fall through: inert.
    } catch (e) { /* private mode: gate works for this page view only via the param */ }
  }

  function isEnabled() {
    if (window.FOOTFACTS_FORCE === true) return true;
    // Stored cohorts are re-validated: revocation = removal from COHORT_CONFIG.
    if (isRegistered(storageGet())) return true;
    // Fallback for storage-less browsers: honor a registered param for this page view.
    return isRegistered(paramValue());
  }

  function getCohort() {
    const stored = storageGet();
    if (isRegistered(stored)) return stored;
    const value = paramValue();
    if (isRegistered(value)) return value;
    // Reserved pseudo-cohort under the simulator's force flag — deliberately not
    // registered, so it gets standard behavior (no flags).
    return window.FOOTFACTS_FORCE === true ? 'test' : null;
  }

  function cohortFlag(flag) {
    const cohort = getCohort();
    const cfg = cohort !== null ? COHORT_CONFIG[cohort] : null;
    return !!(cfg && cfg[flag]);
  }
  let dialog = null;
  // Body scroll is locked via the theme's own html[scroll-lock] convention while the
  // dialog is open; track whether WE set it so we never clear someone else's lock.
  let scrollLockedByUs = false;

  const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  // Status markup shared by the modal and the standalone page section
  // (sections/foot-facts-page.liquid) so loading/error copy exists exactly once.
  function loadingHtml() {
    return '<div class="foot-facts-status">Loading your Foot Facts&hellip;</div>';
  }

  function errorHtml(err, supportEmail) {
    const code = err && err.code;
    const friendly = code === 'SCAN_INVALID' || code === 'SCAN_NOT_FOUND'
      ? 'Looks like your scan ID is invalid. Please try again.'
      : 'Sorry &mdash; we couldn&rsquo;t load your Foot Facts right now. Please try again.';
    const email = escapeHtml(supportEmail || config.supportEmail || 'care@fitmyfoot.com');
    return '<div class="foot-facts-status">' + friendly +
      '<br>If this persists, contact us at <a href="mailto:' + email + '">' + email + '</a>.</div>';
  }

  // Share links target the standalone page, never the PDP (decision D15).
  function shareUrl() {
    if (!lastScanId) return null;
    return window.location.origin + config.sharePath + '?scan=' + encodeURIComponent(lastScanId);
  }

  function ensureDialog() {
    if (dialog) return dialog;
    dialog = document.createElement('dialog');
    dialog.className = 'foot-facts-dialog';
    dialog.setAttribute('aria-label', 'Your Foot Facts');
    dialog.innerHTML =
      '<div class="foot-facts-dialog__bar">' +
      '<button type="button" class="foot-facts-dialog__share" hidden>Copy share link</button>' +
      '<button type="button" class="foot-facts-dialog__close" aria-label="Close">&times;</button>' +
      '</div>' +
      '<div class="foot-facts-dialog__body"></div>';
    dialog.addEventListener('click', (event) => {
      // A click on the backdrop registers on the <dialog> element itself.
      if (event.target === dialog) dialog.close();
    });
    dialog.querySelector('.foot-facts-dialog__close').addEventListener('click', () => dialog.close());
    const shareButton = dialog.querySelector('.foot-facts-dialog__share');
    shareButton.addEventListener('click', () => {
      const url = shareUrl();
      if (!url) return;
      const confirmCopied = () => {
        shareButton.textContent = 'Link copied ✓';
        setTimeout(() => { shareButton.textContent = 'Copy share link'; }, 2500);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(confirmCopied, () => window.prompt('Copy this link:', url));
      } else {
        window.prompt('Copy this link:', url);
      }
    });
    dialog.addEventListener('close', () => {
      if (scrollLockedByUs) {
        document.documentElement.removeAttribute('scroll-lock');
        scrollLockedByUs = false;
      }
    });
    document.body.appendChild(dialog);
    return dialog;
  }

  function setBody(html) {
    ensureDialog().querySelector('.foot-facts-dialog__body').innerHTML = html;
  }

  function open() {
    const d = ensureDialog();
    const share = d.querySelector('.foot-facts-dialog__share');
    if (share) share.hidden = !lastScanId;
    if (!d.open) {
      d.showModal();
      if (!document.documentElement.hasAttribute('scroll-lock')) {
        document.documentElement.setAttribute('scroll-lock', '');
        scrollLockedByUs = true;
      }
    }
    d.scrollTop = 0;
  }

  function showFromData(footFacts, opts) {
    if (typeof window.FootFactsRenderEmail === 'undefined') {
      console.error('[FOOT FACTS] foot-facts-render-email.js is not loaded');
      return;
    }
    const gender = opts && opts.gender !== undefined ? opts.gender : config.gender;
    setBody(window.FootFactsRenderEmail.render(footFacts, { gender: gender }));
    open();
  }

  async function showFromScanId(scanId) {
    setBody(loadingHtml());
    open();
    try {
      const result = await window.FootFactsLoader.load({
        scanId: scanId,
        visaToken: config.visaToken || undefined,
      });
      lastFootFacts = result.footFacts;
      lastScanId = (result.scan && result.scan.publicID) || scanId;
      showFromData(result.footFacts);
    } catch (err) {
      // Technical detail stays in the console; customers get friendly copy only.
      console.error('[FOOT FACTS] load failed:', err);
      setBody(errorHtml(err));
    }
  }

  // The trigger button is rendered (hidden) by blocks/foot-facts-report.liquid in its
  // own markup; this component only binds it and reveals it once data exists. House
  // pattern: self-owned UI + event coupling — no DOM injected into other components.
  function bindTrigger() {
    const button = document.querySelector('.foot-facts-view-trigger__button');
    if (button && !button.dataset.footFactsBound) {
      button.dataset.footFactsBound = 'true';
      button.addEventListener('click', () => {
        if (lastFootFacts) showFromData(lastFootFacts);
      });
    }
  }

  function revealTrigger() {
    const trigger = document.querySelector('.foot-facts-view-trigger');
    if (trigger) trigger.hidden = false;
  }

  function init(options) {
    Object.assign(config, options || {});
    // Persist/clear the cohort from the URL before anything reads the gate.
    syncGateFromUrl();

    const bind = () => bindTrigger();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bind);
    } else {
      bind();
    }

    // Path 1 — post-scan, in-memory (dispatched by volumental-widget.liquid).
    // Data is stored regardless; the triggers only surface when the gate is active.
    document.addEventListener('footfacts:ready', (event) => {
      lastFootFacts = event.detail.footFacts;
      if (event.detail.scanId) lastScanId = event.detail.scanId;
      if (!isEnabled()) return;
      bindTrigger();
      revealTrigger();
    });

    // Path 2 — deep link: ?scan=<publicID or internal id> auto-opens the report.
    // Gated (D16 variant b): on the PDP the gate is the single master switch, so a
    // bare ?scan= does nothing for unenabled visitors. (The standalone share page is
    // separate and ungated by design — D15.)
    const scanId = new URLSearchParams(window.location.search).get(config.paramName);
    if (scanId && isEnabled()) {
      const start = () => showFromScanId(scanId);
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
      } else {
        start();
      }
    }
  }

  // Public helpers for other components (e.g. the measurement checker's notification
  // action): data presence check and "open the last computed report". hasData()
  // reports false while the gate is off, which is what keeps the checker's
  // notification action hidden for ungated visitors.
  function hasData() {
    return isEnabled() && lastFootFacts !== null;
  }

  function showLast() {
    if (lastFootFacts) showFromData(lastFootFacts);
  }

  window.FootFactsReport = {
    init,
    showFromData,
    showFromScanId,
    showLast,
    hasData,
    isEnabled,
    getCohort,
    cohortFlag,
    shareUrl,
    loadingHtml,
    errorHtml,
  };
})();
